(()=>{
"use strict";
// Local review prototype. No installation, network transport or default consent.
const offers = Object.freeze({
  free: ['free_starter_journal', '/pages/free-overthinking-journal'],
  ckc: ['calm_kind_clear', '/pages/calm-kind-clear'],
  rp: ['return_point', '/pages/the-return-point'],
  sog: ['still_on_guard', '/pages/still-on-guard-series'],
  aoab: ['always_on_always_behind', '/pages/always-on-always-behind'],
});
const placements = new Set(['image', 'product-button', 'closing']);

// Media played ranges represent playback, unlike currentTime after seeking.
function coverage(ranges, duration) {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  const sorted = ranges.filter(([a,b]) => Number.isFinite(a) && Number.isFinite(b) && b > a)
    .map(([a,b]) => [Math.max(0,a), Math.min(duration,b)]).sort((a,b) => a[0]-b[0]);
  let end = 0, played = 0;
  for (const [a,b] of sorted) { if (b > Math.max(end,a)) played += b-Math.max(end,a); end = Math.max(end,b); }
  return Math.min(1, played/duration);
}

function createCollector({articleId, allowedArticleIds, consent, send}) {
  if (!allowedArticleIds.has(articleId)) throw new Error('Unregistered public article ID');
  if (typeof consent !== 'function' || typeof send !== 'function') throw new Error('Explicit adapters required');
  const started = new Set(), eligible = new Set(), completed = new Set(), seenClicks = new WeakSet();
  function permitted() { try { return consent() === true; } catch { return false; } }
  function emit(name, offer, placement, preview) {
    if (!permitted() || !Object.hasOwn(offers, offer)) return false;
    const params = {article_id:articleId, offer_id:offers[offer][0], placement, schema_version:'1'};
    if (preview) params.preview_id = offers[offer][0] + '_public_preview';
    // Never retry an uncertain delivery or buffer an event for later consent.
    try { const result = send(name, params); if (result?.catch) result.catch(() => {}); return result !== false; }
    catch { return false; }
  }
  return {
    consentChanged() {
      // Conservative per-page policy: a revoked/unknown interval permanently
      // disqualifies completion for previews already started on this page.
      if (!permitted()) eligible.clear();
    },
    click({event, offer, placement, href}) {
      if (!event || !event.isTrusted || typeof event !== 'object' || seenClicks.has(event)) return false;
      seenClicks.add(event);
      if (!Object.hasOwn(offers, offer) || !placements.has(placement)) return false;
      let url; try { url = new URL(href, 'https://talk2tessa.com'); } catch { return false; }
      if (url.origin !== 'https://talk2tessa.com' || url.pathname.replace(/\/$/, '') !== offers[offer][1]) return false;
      return emit(offer === 'free' ? 't2t_free_journal_click' : 't2t_product_click', offer, placement, false);
    },
    playing(offer) {
      if (started.has(offer)) return false;
      // Mark observed interaction even without permission: do not replay after opt-in.
      started.add(offer);
      const accepted = emit('t2t_preview_start', offer, 'audio-player', true);
      if (accepted) eligible.add(offer);
      return accepted;
    },
    ended(offer, ranges, duration) {
      if (!eligible.has(offer) || completed.has(offer)) return false;
      // Completion means ended with >=95% played coverage; never attention/comprehension.
      if (coverage(ranges, duration) < 0.95) return false;
      completed.add(offer);
      return emit('t2t_preview_complete', offer, 'audio-player', true);
    },
  };
}

const mounted = new WeakMap();

// Explicitly mounted by a future reviewed theme entry point. No automatic startup.
function mountCardTracking(root, options) {
  if (mounted.has(root)) return mounted.get(root);
  const collector = createCollector(options);
  const consentChanged = () => collector.consentChanged();
  const onClick = event => {
    if (event.type === 'auxclick' && event.button !== 1) return;
    const link = event.target?.closest?.('a[data-t2t-cta]');
    if (!link || !root.contains(link)) return;
    collector.consentChanged();
    collector.click({event, offer:link.closest('[data-t2t-offer]')?.dataset.t2tOffer,
      placement:link.dataset.t2tCta, href:link.getAttribute('href')});
  };
  const onMedia = event => {
    const audio = event.target;
    if (!event.isTrusted || audio?.tagName !== 'AUDIO' || !root.contains(audio)) return;
    const offer = audio.dataset.t2tPreview;
    if (!offer || !Object.hasOwn(options.previewSources, offer)) return;
    // Only reviewed public previews, never arbitrary media URLs or user content.
    if (audio.currentSrc !== options.previewSources[offer]) return;
    collector.consentChanged();
    if (event.type === 'playing') collector.playing(offer);
    if (event.type === 'ended') {
      const ranges = Array.from({length:audio.played.length},(_,i)=>[audio.played.start(i),audio.played.end(i)]);
      collector.ended(offer,ranges,audio.duration);
    }
  };
  root.addEventListener('click',onClick,true);
  root.addEventListener('auxclick',onClick,true);
  root.addEventListener('playing',onMedia,true);
  root.addEventListener('ended',onMedia,true);
  root.addEventListener('visitorConsentCollected',consentChanged);
  const binding={consentChanged,dispose(){
    root.removeEventListener('click',onClick,true);root.removeEventListener('auxclick',onClick,true);
    root.removeEventListener('playing',onMedia,true);root.removeEventListener('ended',onMedia,true);
    root.removeEventListener('visitorConsentCollected',consentChanged);mounted.delete(root);
  }};
  mounted.set(root,binding);
  return binding;
}

const destination = 'G-TEQRFML1TZ';
const names = new Set(['t2t_free_journal_click','t2t_product_click','t2t_preview_start','t2t_preview_complete']);
const keys = new Set(['article_id','offer_id','placement','preview_id','schema_version']);
const transportOffers = new Set(['free_starter_journal','calm_kind_clear','return_point','still_on_guard','always_on_always_behind']);
const transportPlacements = new Set(['image','product-button','closing','audio-player']);

// Uses the existing storefront Google tag only. Does not install/configure one.
function createGoogleTransport(win, allowedArticleIds) {
  const consent = () => {
    try {return win.Shopify?.customerPrivacy?.analyticsProcessingAllowed() === true;}
    catch {return false;}
  };
  const send = (name, params) => {
    if (!consent() || typeof win.gtag !== 'function' || !names.has(name)) return false;
    if (!params || Object.keys(params).some(k=>!keys.has(k))) return false;
    if (!allowedArticleIds.has(params.article_id) || !transportOffers.has(params.offer_id) || !transportPlacements.has(params.placement) || params.schema_version !== '1') return false;
    if (name === 't2t_free_journal_click' && params.offer_id !== 'free_starter_journal' || name !== 't2t_free_journal_click' && params.offer_id === 'free_starter_journal') return false;
    if (name === 't2t_product_click' || name === 't2t_free_journal_click') {
      if (params.placement === 'audio-player' || params.preview_id !== undefined) return false;
    } else if (params.placement !== 'audio-player' || params.preview_id !== params.offer_id + '_public_preview') return false;
    // No custom client/session IDs, internal UTMs, config commands or baseline events.
    win.gtag('event',name,{...params,send_to:destination});
    return true; // Accepted by existing tag function, not a delivery acknowledgement.
  };
  return {consent,send};
}

try{
const articleId=document.currentScript?.dataset.t2tArticleId;
const allowedArticleIds=new Set(["gid://shopify/Article/748871680333","gid://shopify/Article/749101220173","gid://shopify/Article/749834109261","gid://shopify/Article/747882905933","gid://shopify/Article/749931528525","gid://shopify/Article/748798050637","gid://shopify/Article/750962049357","gid://shopify/Article/749832864077","gid://shopify/Article/748738904397","gid://shopify/Article/750967259469","gid://shopify/Article/749030408525","gid://shopify/Article/750939472205","gid://shopify/Article/750967980365","gid://shopify/Article/750946779469","gid://shopify/Article/750945272141","gid://shopify/Article/750952120653","gid://shopify/Article/750973976909","gid://shopify/Article/750946713933","gid://shopify/Article/750942454093","gid://shopify/Article/748656623949","gid://shopify/Article/750125351245","gid://shopify/Article/750942191949","gid://shopify/Article/750942126413","gid://shopify/Article/750954152269","gid://shopify/Article/749045678413","gid://shopify/Article/750966702413","gid://shopify/Article/750963753293","gid://shopify/Article/750071808333","gid://shopify/Article/750969815373","gid://shopify/Article/750970044749","gid://shopify/Article/750975025485","gid://shopify/Article/750965752141","gid://shopify/Article/749030474061","gid://shopify/Article/748889669965","gid://shopify/Article/748732186957","gid://shopify/Article/750942355789","gid://shopify/Article/748736315725","gid://shopify/Article/750945304909","gid://shopify/Article/750956609869","gid://shopify/Article/750964048205"]);
if(allowedArticleIds.has(articleId)&&!document.documentElement.hasAttribute('data-t2t-card-tracking-mounted')){
 const adapter=createGoogleTransport(window,allowedArticleIds);
 mountCardTracking(document,{articleId,allowedArticleIds,previewSources:{"rp":"https://talk2tessa.com/cdn/shop/t/25/assets/sales-return-for-a-replaying-mind.mp3?v=106862189405251105581786802300","aoab":"https://cdn.shopify.com/s/files/1/0912/8004/8461/files/always-on-always-behind-audio-preview_c94ded2e-e247-4c6a-852a-ab258c254d36.mp3?v=1784445181","sog":"https://cdn.shopify.com/s/files/1/0912/8004/8461/files/still-on-guard-audio-preview.mp3?v=1781341736"},...adapter});
 document.documentElement.setAttribute('data-t2t-card-tracking-mounted','1');
}
}catch{/* Fail closed; never affect navigation or playback. */}
})();
